import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchLegalStatus } from '../store/legalSlice';
import LegalDocumentSheet from './LegalDocumentSheet';
import { changeLanguage, getChosenLanguage } from '../../../i18n';
import { i18nAPI, userLanguageAPI } from '../../../i18n/useLanguageSync';
import logger from '../../../core/logger/logger';
import '../styles/legal.css';

/**
 * Settings sheet from the balance bar: interface language + legal documents to re-read (accepted ones are marked).
 * Language: «Auto» follows Telegram; a chosen one is stored locally and in users.language (the bot writes in it too).
 */
const SettingsModal = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { documents, loading } = useSelector((state) => state.legal);
  const userId = useSelector((state) => state.user?.data?.id);
  const [visible, setVisible] = useState(false);
  const [reading, setReading] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [chosen, setChosen] = useState(() => getChosenLanguage());
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    setVisible(true);
    dispatch(fetchLegalStatus());
    let alive = true;
    i18nAPI.languages()
      .then((list) => { if (alive) setLanguages(list); })
      .catch(() => {});
    return () => { alive = false; };
  }, [dispatch]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const selectLanguage = async (code) => {
    if (switching || (code || null) === (chosen || null)) return;
    setSwitching(true);
    try {
      await changeLanguage(code, { persist: true });
      setChosen(code || null);
      // document titles come translated from the server
      dispatch(fetchLegalStatus());
      userLanguageAPI.set(code || null).catch((e) => logger.warn('[i18n] save language failed', { message: e?.message }));
    } finally {
      setSwitching(false);
    }
  };

  const options = [{ code: null, label: t('settings.language_auto', 'Auto (Telegram)') }]
    .concat(languages.map((l) => ({ code: l.code, label: l.nativeName || l.name || l.code })));

  return (
    <div className="legal-settings-overlay" onClick={handleClose}>
      <div className={`legal-settings ${visible ? 'visible' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="legal-settings__header">
          <h2 className="legal-settings__title">{t('settings.title', 'Settings')}</h2>
          <button type="button" className="legal-doc__close" onClick={handleClose} aria-label={t('common.close', 'Close')}>
            <img src="/close.png" alt="" />
          </button>
        </div>

        <div className="legal-settings__body">
          {languages.length > 0 && (
            <>
              <p className="legal-settings__section">{t('settings.language', 'Language')}</p>
              <div className="legal-settings__langs" role="radiogroup" aria-label={t('settings.language', 'Language')}>
                {options.map((o) => {
                  const active = (o.code || null) === (chosen || null);
                  return (
                    <button
                      key={o.code || 'auto'}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      className={`legal-settings__lang${active ? ' is-active' : ''}`}
                      disabled={switching}
                      onClick={() => selectLanguage(o.code)}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <p className="legal-settings__section">{t('settings.documents', 'Documents')}</p>
          {!documents.length && loading && <p className="legal-settings__hint">{t('common.loading', 'Loading…')}</p>}
          {documents.map((d) => (
            <button key={d.id} type="button" className="legal-settings__row" onClick={() => setReading(d)}>
              <span className="legal-settings__row-title">{d.title}</span>
              {d.accepted && <span className="legal-settings__badge">{t('settings.accepted', 'Accepted')}</span>}
              <span className="legal-settings__chevron" aria-hidden="true">›</span>
            </button>
          ))}

          {userId && <p className="legal-settings__hint">{t('settings.player_id', 'Player ID: {{id}}', { id: userId })}</p>}
        </div>
      </div>

      {reading && (
        <div onClick={(e) => e.stopPropagation()}>
          <LegalDocumentSheet documentId={reading.id} title={reading.title} onClose={() => setReading(null)} />
        </div>
      )}
    </div>
  );
};

export default SettingsModal;
